import { AppRoutes } from '../../common/constants';
import { Box } from '@mui/material';
import { DEFAULT_ERROR_MESSAGE } from '../../common/lib/fulcrumApi';
import { EditOtherDescriptionCell } from '../fnaSuretyshipPage';
import { FNADetails, FnaKeypersonParty } from '../../common/types/analyses';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { JSX, useEffect, useMemo } from 'react';
import { LoadingButton } from '@mui/lab';
import {
  makeBooleanColumn,
  makeEstateDutyColumns,
  makeNumberColumn,
} from '../../common/helpers/dataGrid';
import { useFnaPartiesUpdater } from '../hooks/useFnaPartiesUpdater';
import { useIsFnaQuoted } from '../hooks/useIsFnaQuoted';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useUpdateFna } from '../../common/components/mutations';
import Content from '../../common/components/content';
import DataGrid from '../../common/components/dataGrid';
import DataGridDeleteAction from '../../common/components/dataGrid/dataGridDeleteAction';
import FnaPageHeaderSummary from '../components/fnaPageHeaderSummary';
import Layout from '../../common/components/layout';
import LayoutLoadingState from '../../common/components/layout/layoutLoadingState';
import StepFooter from '../../common/components/stepFooter';

/**
 * Mapping FNA keyperson party number fields name to display name
 */
const editableKeypersonFieldNames: Array<[keyof FnaKeypersonParty, string]> = [
  ['riskEmployeeRecuitmentRelocation', 'Need - Employee recruitment or relocation'],
  ['riskEmployeeTraining', 'Need - Employee training'],
  ['riskProjectRestart', 'Need - Restart projects'],
  ['riskIncomeAdjustments', 'Need - Decrease in gross income'],
  ['riskLoanAccounts', 'Need - Loan accounts'],
  ['existingProvision', 'Existing provision'],
  ['riskOther', 'Need - Other'],
];

/**
 * Mapping FNA keyperson party boolean fields name to display name
 */
const editableKeypersonBooleanFieldNames: Array<[keyof FnaKeypersonParty, string]> = [
  ['isSection11w', 'Section 11(w)(ii) applies'],
  ['disabilityBenefit', 'Disability benefits'],
];

/**
 * Keyperson edit form for the FNA
 */
const FnaKeypersonPage = (): JSX.Element => {
  const { id } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { mutateAsync, isPending: mutationIsLoading } = useUpdateFna();
  const { data: fna } = useQuery<FNADetails>({
    queryKey: [`/FNADetail/${id}`],
    enabled: Boolean(id),
  });
  const { processRowUpdate, parties, getRowId, checkIfCellIsEditable, deleteParty } =
    useFnaPartiesUpdater(fna?.keypersonParties);

  const hasSection11 = parties.some((party) => party.isSection11w);
  const hasEstateDuty = parties.some((party) => party.isEstateduty);

  // Do not allow to edit needs if FNA is quoted (redirect to the FNA page)
  const { isKeypersonQuoted } = useIsFnaQuoted(Number(id));
  useEffect(() => {
    if (isKeypersonQuoted) {
      navigate(`${AppRoutes.FNAs}/${id}`);
    }
  }, [isKeypersonQuoted, navigate, id]);

  const columns = useMemo<GridColDef<FnaKeypersonParty>[]>(
    () => [
      {
        field: 'actions',
        type: 'actions',
        getActions: ({ row }: GridRenderCellParams) => [
          <DataGridDeleteAction key="delete" onClick={(): void => deleteParty(row)} />,
        ],
      },
      {
        field: 'partyRoleplayerName',
        headerName: 'Name',
        minWidth: 150,
      },
      {
        field: 'representativeRoleplayerName',
        headerName: 'Representative life',
        minWidth: 150,
      },
      ...editableKeypersonFieldNames.map<GridColDef<FnaKeypersonParty>>(([field, headerName]) =>
        makeNumberColumn({
          field,
          headerName,
        }),
      ),
      {
        field: 'riskOtherDescription',
        headerName: 'Other Description',
        minWidth: 300,
        editable: true,
        renderCell: ({ row }) => (
          <span className="whitespace-pre-line">
            {row.riskOther ? row.riskOtherDescription : ''}
          </span>
        ),
        renderEditCell: EditOtherDescriptionCell,
      },
      ...makeEstateDutyColumns(processRowUpdate),
      ...editableKeypersonBooleanFieldNames.map<GridColDef<FnaKeypersonParty>>(
        ([field, headerName]) =>
          makeBooleanColumn({
            field,
            headerName,
            processRowUpdate,
          }),
      ),
    ],
    [processRowUpdate, deleteParty],
  );

  if (!fna) {
    return <LayoutLoadingState />;
  }

  const fnaRoleplayers =
    fna.roleplayers.map(({ roleplayerName }) => roleplayerName).join(', ') || '-';
  const breadcrumbs = [
    { label: 'Financial needs analyses', href: AppRoutes.FNAs },
    { label: fnaRoleplayers, href: `${AppRoutes.FNAs}/${fna.id}` },
  ];

  const handleSave = async (): Promise<void> => {
    try {
      await mutateAsync({
        ...fna,
        keypersonParties: parties,
      });
    } catch (error) {
      enqueueSnackbar((error as Error).message || DEFAULT_ERROR_MESSAGE, { variant: 'error' });
    }
  };

  const handleSaveAndExit = async (): Promise<void> => {
    await handleSave();
    navigate(AppRoutes.FNAs);
  };

  const handleCalculation = async (): Promise<void> => {
    if (parties.some((party) => party.riskOther && !party.riskOtherDescription)) {
      enqueueSnackbar('Please enter a description for the "Other" field', { variant: 'error' });
      return;
    }
    await handleSave();
    navigate(`${AppRoutes.FNAs}/${fna.id}/keyperson/needs`);
  };

  const handleClickBack = (): void => {
    navigate(`${AppRoutes.FNAs}/${fna.id}?tab=3`);
  };

  return (
    <Layout breadcrumbs={breadcrumbs}>
      <Content>
        <Box className="md:flex justify-between items-start md:space-x-4 space-y-8 md:space-y-0">
          <FnaPageHeaderSummary fna={fna} title="Key person needs" />
          <LoadingButton
            loading={mutationIsLoading}
            className="flex-shrink-0"
            variant="contained"
            color="primary"
            onClick={handleSaveAndExit}
          >
            Save and exit
          </LoadingButton>
        </Box>
        <DataGrid
          actionsVisibleOnHover={false}
          className="mt-12"
          rows={parties}
          columns={columns}
          enableQuickFilter={false}
          getRowId={getRowId}
          isCellEditable={checkIfCellIsEditable}
          processRowUpdate={processRowUpdate}
        />
        <div className="space-y-4 mt-4">
          {hasSection11 && (
            <p>
              * The policy premiums will qualify as a tax deduction and will be tax deductible. The
              proceeds of the policy will be included in the gross income of the business and
              therefore potentially taxable. Where the sum insured needs to be increased to provide
              for the income tax liability, it should be divided by a factor that is equal to 100
              less the tax rate of the business. The tax rate of a company or close corporation is
              27% and therefore the factor will be 0.73.
            </p>
          )}
          {!hasSection11 && (
            <p>
              * The policy premiums will not qualify as a tax deduction. The proceeds will be
              included in the gross income of the business Paragraph (m) of the gross income
              definition. Because the premiums were not deductible, the exemption in terms of
              Section 10(1)(gH) will apply and the proceeds will be tax-free.
            </p>
          )}
          {hasEstateDuty && (
            <p>
              ** The policy proceeds will be subject to estate duty in the estate of the deceased
              life insured. The sum insured should be adjusted to make provision for the estate duty
              as the executor will claim the proportionate estate duty from the policyholder (the
              business). Estate duty is levied at 20% or 25%. To make provision for this, the sum
              insured must be divided by either 0.8 or 0.75.
            </p>
          )}
        </div>
        <StepFooter
          isLoading={false}
          nextButtonTitle="Calculate cover required"
          onClickNext={handleCalculation}
          onClickBack={handleClickBack}
        />
      </Content>
    </Layout>
  );
};

export default FnaKeypersonPage;
